"use client";

import { loader } from "@monaco-editor/react";
import type * as Monaco from "monaco-editor";
import { type ReactNode, useEffect, useState } from "react";

import { CodeBlockCopyButton } from "../ai-elements/code-block";
import { setupSqlMonaco } from "../lib/sql-monaco";
import { cn } from "../lib/utils";

let monacoPromise: Promise<typeof Monaco> | null = null;

/**
 * Lazily resolves the shared Monaco instance with the SQL language + themes
 * registered. Rides the same `@monaco-editor/react` `loader` singleton that
 * `preloadSqlEditor` warms at app idle, so a chat message usually renders
 * against an already-loaded Monaco.
 */
const getSqlMonaco = () => {
  monacoPromise ??= loader.init().then((monaco) => {
    setupSqlMonaco(monaco);
    return monaco;
  });
  return monacoPromise;
};

/**
 * Tracks the document's dark mode by watching the `dark` class on the root
 * element (written by the app's theme store). Reading the DOM keeps this
 * package free of any app-store dependency.
 */
const useIsDark = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof document === "undefined"
      ? true
      : document.documentElement.classList.contains("dark"),
  );

  useEffect(() => {
    const root = document.documentElement;
    const update = () => setIsDark(root.classList.contains("dark"));
    update();
    const observer = new MutationObserver(update);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  return isDark;
};

type SqlCodeBlockProps = {
  code: string;
  className?: string;
  /** Extra hover actions rendered next to the copy button (app-injected). */
  actions?: ReactNode;
};

/**
 * Read-only SQL display that reuses the dataset editor's exact Monaco tokenizer
 * and `index-dark` / `index-light` themes via static `monaco.editor.colorize`
 * (no editor instance). Renders a hover copy button plus any injected
 * {@link SqlCodeBlockProps.actions}.
 */
export const SqlCodeBlock = ({
  code,
  className,
  actions,
}: SqlCodeBlockProps) => {
  const isDark = useIsDark();
  const [html, setHtml] = useState("");

  useEffect(() => {
    let cancelled = false;
    getSqlMonaco().then(async (monaco) => {
      monaco.editor.setTheme(isDark ? "index-dark" : "index-light");
      const colorized = await monaco.editor.colorize(code, "sql", {});
      if (!cancelled) setHtml(colorized);
    });
    return () => {
      cancelled = true;
    };
  }, [code, isDark]);

  return (
    <div
      className={cn(
        "group relative w-full overflow-hidden rounded-md border bg-background text-foreground",
        className,
      )}
    >
      {html ? (
        <div
          className="overflow-x-auto whitespace-pre p-4 font-mono text-sm [&_.mtk1]:text-foreground"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: "colorize output is trusted Monaco markup."
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <pre className="overflow-x-auto whitespace-pre p-4 font-mono text-sm">
          {code}
        </pre>
      )}
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <CodeBlockCopyButton code={code} />
        {actions}
      </div>
    </div>
  );
};
